import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Calendar as CalendarIcon, List, LayoutDashboard, Bell, MessageSquare, Trash2, Upload, Eye, Download, Paperclip } from 'lucide-react';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TaskManagementBase({ user, moduleName, moduleTitle, moduleDescription }) {
  const navigate = useNavigate();
  
  // Check if user exists
  if (!user || !user.id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border-2 border-red-500 p-6 rounded">
          <h2 className="text-xl font-bold text-red-700">Error: User Not Found</h2>
          <p className="text-red-600 mt-2">The user object is missing. Please log in again.</p>
        </div>
      </div>
    );
  }
  
  // View state
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, list, calendar
  
  // Data state
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ pending: 0, inProgress: 0, completed: 0, total: 0 });
  
  // Filter state
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    task_name: '',
    description: '',
    assignee: '',
    assignee_name: '',
    due_date: '',
    start_date: '',
    end_date: ''
  });
  
  // Comment state
  const [commentText, setCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Delete authorization states
  const [deleteAuthDialogOpen, setDeleteAuthDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Attachment states (Vendor Data pattern)
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchTasks();
    fetchNotifications();
    fetchUnreadCount();
    fetchStats();
  }, []);

  // Auto-refresh every 5 seconds, but pause when dialogs are open
  const isUserEditing = createDialogOpen || viewDialogOpen || notificationsPanelOpen;
  useAutoRefresh(() => {
    fetchTasks();
    fetchNotifications();
    fetchStats();
  }, 5, isUserEditing);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out Admin and Yahya Nasikwala
      const filteredUsers = response.data.filter(u => 
        u.username !== 'admin@mntxtiles.com' && 
        u.name !== 'Yahya Nasikwala'
      );
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleStatCardClick = (status) => {
    setActiveView('list');
    setSelectedStatus(status);
  };

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (selectedAssignee) params.assignee = selectedAssignee;
      
      const encodedModule = encodeURIComponent(moduleName);
      const response = await axios.get(`${API}/tasks/${encodedModule}`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async (limit = 10, skip = 0) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications?limit=${limit}&skip=${skip}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (skip === 0) {
        // First load, replace all notifications
        setNotifications(response.data);
      } else {
        // Load more, append to existing
        setNotifications(prev => [...prev, ...response.data]);
      }
      
      // Also fetch unread count
      await fetchUnreadCount();
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleNotificationClick = (taskId) => {
    // Find the task and open it in view mode
    const task = tasks.find(t => t.task_id === taskId);
    if (task) {
      setSelectedTask(task);
      setViewDialogOpen(true);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const encodedModule = encodeURIComponent(moduleName);
      const response = await axios.get(`${API}/tasks/${encodedModule}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Map backend response to frontend state
      const data = response.data;
      setStats({
        total: data.total_tasks || 0,
        pending: data.status_counts?.pending || 0,
        inProgress: data.status_counts?.in_progress || 0,
        completed: data.status_counts?.completed || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreateTask = async () => {
    // Validation
    if (!formData.task_name.trim()) {
      toast.error('Task name is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!formData.assignee) {
      toast.error('Assignee is required');
      return;
    }
    if (!formData.due_date) {
      toast.error('Due date is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const taskData = {
        task_name: formData.task_name,
        description: formData.description,
        assignee: formData.assignee,
        assignee_name: formData.assignee_name,
        due_date: formData.due_date,
        start_date: null,
        end_date: null
      };

      const encodedModule = encodeURIComponent(moduleName);
      await axios.post(`${API}/tasks/${encodedModule}/create`, taskData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreateDialogOpen(false);
      resetForm();
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleAssigneeChange = (userId) => {
    const selectedUser = users.find(u => u.id === userId);
    setFormData({
      ...formData,
      assignee: userId,
      assignee_name: selectedUser ? selectedUser.name : ''
    });
  };

  const handleViewTask = async (task) => {
    setSelectedTask(task);
    setViewDialogOpen(true);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTask) return;

    try {
      const token = localStorage.getItem('token');
      const updates = { status: newStatus };
      
      // Auto-set start date when status changes to "In Progress"
      if (newStatus === 'In Progress' && !selectedTask.start_date) {
        const today = new Date().toISOString().split('T')[0];
        updates.start_date = today;
      }
      
      // Auto-set end date when status changes to "Completed"
      if (newStatus === 'Completed' && !selectedTask.end_date) {
        const today = new Date().toISOString().split('T')[0];
        updates.end_date = today;
      }

      await axios.put(
        `${API}/tasks/${moduleName}/${selectedTask.task_id}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchTasks();
      fetchStats();
      
      // Refresh task details
      const response = await axios.get(`${API}/tasks/${moduleName}/${selectedTask.task_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTask(response.data);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !selectedTask) return;

    try {
      setAddingComment(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('comment_text', commentText);

      await axios.post(
        `${API}/tasks/${moduleName}/${selectedTask.task_id}/comment`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCommentText('');
      toast.success('Comment added successfully');
      
      // Refresh task details
      const response = await axios.get(`${API}/tasks/${moduleName}/${selectedTask.task_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTask(response.data);
      
      // Also refresh the task list
      await fetchTasks();
      
      setAddingComment(false);
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment: ' + (error.response?.data?.detail || error.message));
      setAddingComment(false);
    }
  };

  const handleUpdateTask = async () => {
    // This function handles saving any changes and closing the modal
    if (commentText.trim()) {
      // If there's a comment, add it first
      await handleAddComment();
    } else {
      // Just close the modal
      toast.success('Task updated successfully');
      setViewDialogOpen(false);
    }
  };

  // ==================== ATTACHMENT HANDLERS (Vendor Data Pattern) ====================
  
  const handleAttachmentUpload = async () => {
    if (!attachmentFile || !selectedTask) return;

    setIsUploadingAttachment(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', attachmentFile);

      await axios.post(
        `${API}/tasks/${moduleName}/${selectedTask.task_id}/attachment`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Attachment uploaded successfully');
      setAttachmentFile(null);
      setAttachmentPreview(null);
      
      // Clear file input
      const fileInput = document.getElementById('attachment-upload');
      if (fileInput) fileInput.value = '';
      
      // Refresh task details
      const response = await axios.get(`${API}/tasks/${moduleName}/${selectedTask.task_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTask(response.data);
      
      // Also refresh the task list
      await fetchTasks();
    } catch (error) {
      console.error('Failed to upload attachment:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (url) => {
    if (!selectedTask) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/tasks/${moduleName}/${selectedTask.task_id}/attachment?url=${encodeURIComponent(url)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Attachment deleted successfully');
      
      // Refresh task details
      const response = await axios.get(`${API}/tasks/${moduleName}/${selectedTask.task_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTask(response.data);
      
      // Also refresh the task list
      await fetchTasks();
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      toast.error('Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (url, filename) => {
    try {
      const fullUrl = `${BACKEND_URL}${url}?download=true`;
      const token = localStorage.getItem('token');
      
      const response = await fetch(fullUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleViewAttachment = (url) => {
    const fullUrl = `${BACKEND_URL}${url}`;
    window.open(fullUrl, '_blank');
  };

  // ==================== END ATTACHMENT HANDLERS ====================

  const handleInlineEdit = (taskId, field, currentValue) => {
    // Only assignee can edit these fields
    const task = tasks.find(t => t.id === taskId);
    if (task && task.assignee !== user.id) {
      toast.error('Only the assignee can update this field');
      return;
    }

    setEditingTaskId(taskId);
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const handleInlineSave = async (taskId, task_id) => {
    try {
      const token = localStorage.getItem('token');
      const updates = {};
      updates[editingField] = editValue;

      await axios.put(
        `${API}/tasks/${moduleName}/${task_id}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEditingTaskId(null);
      setEditingField(null);
      setEditValue('');
      fetchTasks();
    } catch (error) {
      console.error('Failed to update field:', error);
      toast.error('Failed to update: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleInlineStatusChange = async (task, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const updates = { status: newStatus };
      
      // Auto-set start date when status changes to "In Progress"
      if (newStatus === 'In Progress' && !task.start_date) {
        const today = new Date().toISOString().split('T')[0];
        updates.start_date = today;
        toast.info('Start date automatically set to today');
      }
      
      // Auto-set end date when status changes to "Completed"
      if (newStatus === 'Completed' && !task.end_date) {
        const today = new Date().toISOString().split('T')[0];
        updates.end_date = today;
        toast.info('End date automatically set to today');
      }

      await axios.put(
        `${API}/tasks/${moduleName}/${task.task_id}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Status updated successfully');
      fetchTasks();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setDeleteAuthDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/tasks/${moduleName}/${taskToDelete.task_id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`Task ${taskToDelete.task_id} deleted successfully`);
      setDeleteAuthDialogOpen(false);
      setTaskToDelete(null);
      
      // Refresh the list
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task: ' + (error.response?.data?.detail || error.message));
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the notification in local state to remove blue dot
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      
      // Update unread count from server response
      if (response.data.unread_count !== undefined) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      task_name: '',
      description: '',
      assignee: '',
      assignee_name: '',
      due_date: '',
      start_date: '',
      end_date: ''
    });
  };

  const filteredTasks = tasks.filter(task => {
    if (selectedStatus && task.status !== selectedStatus) return false;
    if (searchTerm && !task.task_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !task.task_id.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-black">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/task-management')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {moduleTitle}
                </h1>
                <p className="text-sm text-gray-600 mt-1">{moduleDescription}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Notifications */}
              <div className="relative">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setNotificationsPanelOpen(!notificationsPanelOpen)}
                  className="relative"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
                
                {/* Notifications Dropdown */}
                {notificationsPanelOpen && (
                  <>
                    {/* Click outside overlay */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setNotificationsPanelOpen(false)}
                    />
                    
                    {/* Notification panel */}
                    <div className="absolute right-0 mt-2 w-80 bg-white border-2 border-black shadow-lg z-50 max-h-96 overflow-y-auto">
                      <div className="p-4 border-b-2 border-black">
                        <h3 className="font-bold">Notifications</h3>
                      </div>
                      {notifications.length === 0 ? (
                        <div className="p-4 text-center text-gray-500">
                          No notifications
                        </div>
                      ) : (
                        <div>
                          {notifications.map(notif => (
                            <div
                              key={notif.id}
                              className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 ${
                                !notif.is_read ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => {
                                markNotificationAsRead(notif.id);
                                handleNotificationClick(notif.task_id);
                                setNotificationsPanelOpen(false);
                              }}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="font-semibold text-sm">{notif.task_name}</p>
                                  <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(notif.created_at).toLocaleString()}
                                  </p>
                                </div>
                                {!notif.is_read && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1"></div>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {/* Load More Button */}
                          {notifications.length > 0 && notifications.length < 50 && notifications.length % 10 === 0 && (
                            <div className="p-4 text-center border-t-2 border-gray-200">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  fetchNotifications(10, notifications.length);
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                              >
                                Load More
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <Button onClick={() => setCreateDialogOpen(true)} className="bg-black text-white hover:bg-gray-800">
                <Plus className="w-4 h-4 mr-2" />
                Create Task
              </Button>
            </div>
          </div>

          {/* View Tabs */}
          <div className="flex gap-2 mt-4">
            <Button
              variant={activeView === 'dashboard' ? 'default' : 'outline'}
              onClick={() => setActiveView('dashboard')}
              className={activeView === 'dashboard' ? 'bg-black text-white' : ''}
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <Button
              variant={activeView === 'list' ? 'default' : 'outline'}
              onClick={() => setActiveView('list')}
              className={activeView === 'list' ? 'bg-black text-white' : ''}
            >
              <List className="w-4 h-4 mr-2" />
              List View
            </Button>
            <Button
              variant={activeView === 'calendar' ? 'default' : 'outline'}
              onClick={() => setActiveView('calendar')}
              className={activeView === 'calendar' ? 'bg-black text-white' : ''}
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendar View
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Dashboard View */}
        {activeView === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div 
                className="bg-white border-2 border-black p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => {
                  setActiveView('list');
                  setSelectedStatus('');
                }}
              >
                <h3 className="text-sm font-semibold text-gray-600 uppercase">Total Tasks</h3>
                <p className="text-3xl font-bold mt-2">{stats.total}</p>
              </div>
              <div 
                className="bg-yellow-200 border-2 border-black p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => handleStatCardClick('Pending')}
              >
                <h3 className="text-sm font-semibold text-gray-600 uppercase">To Do</h3>
                <p className="text-3xl font-bold mt-2">{stats.pending}</p>
              </div>
              <div 
                className="bg-blue-200 border-2 border-black p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => handleStatCardClick('In Progress')}
              >
                <h3 className="text-sm font-semibold text-gray-600 uppercase">In Progress</h3>
                <p className="text-3xl font-bold mt-2">{stats.inProgress}</p>
              </div>
              <div 
                className="bg-green-200 border-2 border-black p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => handleStatCardClick('Completed')}
              >
                <h3 className="text-sm font-semibold text-gray-600 uppercase">Done</h3>
                <p className="text-3xl font-bold mt-2">{stats.completed}</p>
              </div>
            </div>

            {/* Recent Tasks */}
            <div className="bg-white border-2 border-black p-6">
              <h2 className="text-xl font-bold mb-4">Recent Tasks</h2>
              <div className="space-y-3">
                {tasks.slice(0, 10).map(task => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewTask(task)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm font-semibold">{task.task_id}</span>
                        <span className="font-semibold">{task.task_name}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span>Assignee: {task.assignee_name}</span>
                        <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div>
                      <span className={`px-3 py-1 rounded text-sm font-semibold text-black ${
                        task.status === 'Pending' ? 'bg-yellow-200' :
                        task.status === 'In Progress' ? 'bg-blue-200' :
                        'bg-green-200'
                      }`}>
                        {task.status === 'Pending' ? 'To Do' : 
                         task.status === 'In Progress' ? 'In Progress' : 'Done'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* List View */}
        {activeView === 'list' && (
          <div className="bg-white border-2 border-black p-6">
            {/* Filters */}
            <div className="flex gap-4 mb-6">
              <div className="flex-1">
                <Input
                  placeholder="Search by task name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="w-48">
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'Pending', label: 'To Do' },
                    { value: 'In Progress', label: 'In Progress' },
                    { value: 'Completed', label: 'Done' }
                  ]}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  placeholder="Filter by status"
                />
              </div>
              <div className="w-48">
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Assignees' },
                    ...users.map(u => ({ value: u.id, label: u.name }))
                  ]}
                  value={selectedAssignee}
                  onChange={(value) => {
                    setSelectedAssignee(value);
                    fetchTasks();
                  }}
                  placeholder="Filter by assignee"
                />
              </div>
            </div>

            {/* Tasks Table - JIRA Style */}
            <div className="overflow-x-auto bg-white rounded-lg shadow">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Key</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Task Name</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Assignee</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Due Date</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Start Date</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">End Date</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Reporter</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map(task => (
                    <tr
                      key={task.id}
                      className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-2.5 px-3">
                        <span 
                          className="text-xs font-mono text-blue-600 hover:underline cursor-pointer"
                          onClick={() => handleViewTask(task)}
                        >
                          {task.task_id}
                        </span>
                      </td>
                      
                      <td className="py-2.5 px-3 text-sm font-medium text-gray-900 max-w-xs truncate">
                        {task.task_name}
                      </td>
                      
                      <td className="py-2.5 px-3">
                        <span className="text-sm text-gray-700">{task.assignee_name}</span>
                      </td>
                      
                      {/* Status - Display Only (change in detail modal) */}
                      <td className="py-2.5 px-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded text-black ${
                          task.status === 'Pending' ? 'bg-yellow-200' :
                          task.status === 'In Progress' ? 'bg-blue-200' :
                          'bg-green-200'
                        }`}>
                          {task.status === 'Pending' ? 'To Do' : task.status === 'In Progress' ? 'In Progress' : 'Done'}
                        </span>
                      </td>
                      
                      <td className="py-2.5 px-3">
                        <span className="text-sm text-gray-700">
                          {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </td>
                      
                      {/* Start Date - Auto-set when status changes to In Progress */}
                      <td className="py-2.5 px-3">
                        <span className="text-sm text-gray-700">
                          {task.start_date ? new Date(task.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                        </span>
                      </td>

                      {/* End Date - Auto-set when status changes to Completed */}
                      <td className="py-2.5 px-3">
                        <span className="text-sm text-gray-700">
                          {task.end_date ? new Date(task.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                        </span>
                      </td>

                      <td className="py-2.5 px-3">
                        <span className="text-sm text-gray-700">{task.reporter_name}</span>
                      </td>
                      
                      {/* Delete Button */}
                      <td className="py-2.5 px-3">
                        <button
                          onClick={() => handleDeleteClick(task)}
                          className="px-3 py-1 text-xs font-medium text-red-600 border border-red-600 rounded hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredTasks.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No tasks found
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar View - Monthly with Navigation */}
        {activeView === 'calendar' && (
          <div className="bg-white border-2 border-black p-6">
            {/* Calendar Header with Month/Year and Navigation */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => {
                  const newDate = new Date(calendarDate);
                  newDate.setMonth(newDate.getMonth() - 1);
                  setCalendarDate(newDate);
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <span className="text-2xl">←</span>
              </button>
              
              <h2 className="text-2xl font-bold">
                {calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              
              <button
                onClick={() => {
                  const newDate = new Date(calendarDate);
                  newDate.setMonth(newDate.getMonth() + 1);
                  setCalendarDate(newDate);
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <span className="text-2xl">→</span>
              </button>
            </div>

            {/* Filters Section */}
            <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b-2 border-gray-200">
              {/* User Filter */}
              <div>
                <Label className="font-bold text-sm mb-2 block">Filter by User</Label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Users' },
                    ...users.map(u => ({
                      value: u.id,
                      label: `${u.name} (${u.username})`
                    }))
                  ]}
                  value={selectedAssignee}
                  onChange={setSelectedAssignee}
                  placeholder="Select user..."
                  className="w-full"
                />
              </div>

              {/* Status Filter */}
              <div>
                <Label className="font-bold text-sm mb-2 block">Filter by Status</Label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'Pending', label: 'Pending' },
                    { value: 'In Progress', label: 'In Progress' },
                    { value: 'Completed', label: 'Completed' }
                  ]}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  placeholder="Select status..."
                  className="w-full"
                />
              </div>
            </div>

            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-bold text-gray-600 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {(() => {
                const year = calendarDate.getFullYear();
                const month = calendarDate.getMonth();
                const firstDay = new Date(year, month, 1);
                const lastDay = new Date(year, month + 1, 0);
                const startingDayOfWeek = firstDay.getDay();
                const totalDays = lastDay.getDate();
                
                const calendarDays = [];
                
                // Empty cells before first day
                for (let i = 0; i < startingDayOfWeek; i++) {
                  calendarDays.push(
                    <div key={`empty-${i}`} className="border border-gray-200 p-2 min-h-24 bg-gray-50"></div>
                  );
                }
                
                // Days of the month
                for (let day = 1; day <= totalDays; day++) {
                  const date = new Date(year, month, day);
                  
                  // Apply filters to tasks
                  let filteredTasks = tasks.filter(t => {
                    const taskDate = new Date(t.due_date);
                    const dateMatches = taskDate.toDateString() === date.toDateString();
                    const assigneeMatches = !selectedAssignee || t.assignee === selectedAssignee;
                    const statusMatches = !selectedStatus || t.status === selectedStatus;
                    return dateMatches && assigneeMatches && statusMatches;
                  });
                  
                  const tasksForDate = filteredTasks;
                  
                  const isToday = date.toDateString() === new Date().toDateString();
                  
                  calendarDays.push(
                    <div
                      key={day}
                      className={`border border-gray-200 p-2 min-h-24 ${isToday ? 'bg-blue-50 border-blue-500' : ''}`}
                    >
                      <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-blue-600' : ''}`}>
                        {day}
                      </div>
                      {tasksForDate.map(task => (
                        <div
                          key={task.id}
                          className={`text-xs p-1 mb-1 rounded cursor-pointer truncate text-black ${
                            task.status === 'Pending' ? 'bg-yellow-200 hover:bg-yellow-300' :
                            task.status === 'In Progress' ? 'bg-blue-200 hover:bg-blue-300' :
                            'bg-green-200 hover:bg-green-300'
                          }`}
                          onClick={() => handleViewTask(task)}
                          title={task.task_name}
                        >
                          {task.task_name}
                        </div>
                      ))}
                    </div>
                  );
                }
                
                return calendarDays;
              })()}
            </div>
          </div>
        )}
      </main>

      {/* Create Task Dialog - Using same pattern as FashionDesign */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Task Name *</Label>
              <Input
                value={formData.task_name}
                onChange={(e) => setFormData({ ...formData, task_name: e.target.value })}
                placeholder="Enter task name"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <textarea
                className="w-full border border-gray-300 rounded p-2 min-h-24"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter task description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assignee *</Label>
                <SearchableSelect
                  options={users.map(u => ({ value: u.id, label: u.name }))}
                  value={formData.assignee}
                  onChange={handleAssigneeChange}
                  placeholder="Select assignee"
                />
              </div>

              <div>
                <Label>Reporter</Label>
                <Input value={user.name} disabled className="bg-gray-100" />
              </div>
            </div>

            <div>
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setCreateDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateTask}>
                Create Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View/Edit Task Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedTask && (
            <>
              {/* Header - Simple Title */}
              <DialogHeader className="border-b pb-4">
                <DialogTitle className="text-xl font-semibold text-black">
                  Task Details
                </DialogTitle>
              </DialogHeader>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto space-y-6 py-4">
                {/* Task Details Grid */}
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <div>
                    <p className="text-sm font-medium text-black mb-1">Key</p>
                    <p className="text-sm text-black font-mono">{selectedTask.task_id}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-black mb-1">Task Name</p>
                    <p className="text-sm text-black">{selectedTask.task_name}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-black mb-1">Status</p>
                    {selectedTask.assignee === user.id ? (
                      <SearchableSelect
                        options={[
                          { value: 'Pending', label: 'To Do' },
                          { value: 'In Progress', label: 'In Progress' },
                          { value: 'Completed', label: 'Done' }
                        ]}
                        value={selectedTask.status}
                        onChange={handleStatusChange}
                      />
                    ) : (
                      <span className={`inline-block px-2 py-1 rounded text-sm text-black ${
                        selectedTask.status === 'Pending' ? 'bg-yellow-200' :
                        selectedTask.status === 'In Progress' ? 'bg-blue-200' :
                        'bg-green-200'
                      }`}>
                        {selectedTask.status === 'Pending' ? 'To Do' : 
                         selectedTask.status === 'In Progress' ? 'In Progress' : 'Done'}
                      </span>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium text-black mb-1">Due Date</p>
                    <p className="text-sm text-black">{new Date(selectedTask.due_date).toLocaleDateString()}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-black mb-1">Assignee</p>
                    <p className="text-sm text-black">{selectedTask.assignee_name}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-black mb-1">Reporter</p>
                    <p className="text-sm text-black">{selectedTask.reporter_name}</p>
                  </div>

                  {selectedTask.start_date && (
                    <div>
                      <p className="text-sm font-medium text-black mb-1">Start Date</p>
                      <p className="text-sm text-black">{new Date(selectedTask.start_date).toLocaleDateString()}</p>
                    </div>
                  )}

                  {selectedTask.end_date && (
                    <div>
                      <p className="text-sm font-medium text-black mb-1">End Date</p>
                      <p className="text-sm text-black">{new Date(selectedTask.end_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-black mb-2">Description</p>
                  <p className="text-sm text-black whitespace-pre-wrap">{selectedTask.description}</p>
                </div>

                {/* Attachments Section */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-black mb-3 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Attachments ({selectedTask.attachments?.length || 0})
                  </p>

                  {/* Existing Attachments */}
                  {selectedTask.attachments && selectedTask.attachments.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {selectedTask.attachments.map((attachment, index) => (
                        <div key={index} className="flex items-center justify-between border border-gray-300 p-3 rounded">
                          <div className="flex items-center gap-2 flex-1">
                            <Paperclip className="w-4 h-4 text-black" />
                            <span className="text-sm text-black truncate">{attachment.filename}</span>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleViewAttachment(attachment.url)}
                              className="border-2 border-black text-sm text-black hover:bg-gray-100"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDownloadAttachment(attachment.url, attachment.filename)}
                              className="border-2 border-black text-sm text-black hover:bg-gray-100"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Download
                            </Button>
                            {(selectedTask.assignee === user.id || selectedTask.reporter === user.id) && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDeleteAttachment(attachment.url)}
                                className="border-2 border-black text-sm text-black hover:bg-gray-100"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload New Attachment - Only for assignee/reporter */}
                  {(selectedTask.assignee === user.id || selectedTask.reporter === user.id) && (
                    <div className="space-y-3 border border-gray-300 p-4 rounded">
                      <p className="text-sm font-medium text-black">Upload New Attachment</p>
                      <div className="flex items-center gap-2">
                        <label 
                          htmlFor="attachment-upload" 
                          className="px-4 py-2 border-2 border-black rounded cursor-pointer hover:bg-gray-100 text-sm font-medium inline-block text-black"
                        >
                          Choose File
                        </label>
                        <input 
                          id="attachment-upload"
                          type="file" 
                          accept="*/*"
                          className="hidden"
                          disabled={isUploadingAttachment}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setAttachmentFile(file);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setAttachmentPreview(reader.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                        <span className="text-sm text-black">
                          {attachmentFile ? attachmentFile.name : 'PDF, Images, Excel, PowerPoint, etc.'}
                        </span>
                        {attachmentFile && (
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setAttachmentFile(null);
                              setAttachmentPreview(null);
                              const fileInput = document.getElementById('attachment-upload');
                              if (fileInput) fileInput.value = '';
                            }}
                            className="text-sm text-black border-2 border-black hover:bg-gray-100"
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Remove
                          </Button>
                        )}
                      </div>
                      
                      {attachmentFile && (
                        <div className="flex items-center gap-3 p-3 border border-gray-300 rounded">
                          <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                            {attachmentFile.type.includes('pdf') ? (
                              <span className="text-xs font-bold text-black">PDF</span>
                            ) : attachmentFile.type.includes('sheet') || attachmentFile.name.match(/\.(xlsx?|csv)$/i) ? (
                              <span className="text-xs font-bold text-black">XLS</span>
                            ) : attachmentFile.type.includes('presentation') || attachmentFile.name.match(/\.pptx?$/i) ? (
                              <span className="text-xs font-bold text-black">PPT</span>
                            ) : attachmentFile.type.startsWith('image/') ? (
                              <span className="text-xl">🖼️</span>
                            ) : (
                              <Paperclip className="w-5 h-5 text-black" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-black">{attachmentFile.name}</p>
                            <p className="text-sm text-black">
                              {(attachmentFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                          <Button
                            onClick={handleAttachmentUpload}
                            disabled={isUploadingAttachment}
                            className="border-2 border-black text-sm text-black bg-white hover:bg-gray-100"
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            {isUploadingAttachment ? 'Uploading...' : 'Upload'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Comments */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-black mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Comments ({selectedTask.comments?.length || 0})
                  </p>
                  
                  <div className="space-y-3 mb-4">
                    {selectedTask.comments?.map((comment, index) => (
                      <div key={index} className="border border-gray-300 p-3 rounded">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-sm font-medium text-black">{comment.user}</p>
                            <p className="text-xs text-black">{new Date(comment.timestamp).toLocaleString()}</p>
                          </div>
                        </div>
                        <p className="text-sm text-black">{comment.text}</p>
                      </div>
                    ))}
                  </div>

                  {/* Add Comment */}
                  <div className="flex gap-2">
                    <textarea
                      className="flex-1 border border-gray-300 rounded p-2 min-h-20 text-sm text-black"
                      placeholder="Add a comment..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Footer with Update Button */}
              <div className="border-t pt-4 flex justify-end gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setViewDialogOpen(false)}
                  className="border-2 border-black text-sm text-black hover:bg-gray-100"
                >
                  Close
                </Button>
                <Button 
                  onClick={handleUpdateTask} 
                  disabled={addingComment}
                  className="border-2 border-black bg-black text-white text-sm hover:bg-gray-800"
                >
                  {addingComment ? 'Updating...' : 'Update Task'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Authorization Dialog */}
      <AuthorizationDialog
        open={deleteAuthDialogOpen}
        onOpenChange={setDeleteAuthDialogOpen}
        onAuthorized={handleDeleteConfirm}
        title="Delete Task"
        message={
          taskToDelete 
            ? `Enter authorization code to delete task ${taskToDelete.task_id} - ${taskToDelete.task_name}`
            : 'Enter authorization code to proceed'
        }
        correctCode="notask"
      />

      {/* Attachment viewer removed - files now open in new window directly */}
    </div>
  );
}
